import React, { useMemo, useState } from 'react';

import { CheckOutlined, EditOutlined, CloseOutlined, PlusOutlined, QuestionCircleFilled } from '@ant-design/icons';
import { Tag, Button, Popconfirm, message, Tooltip, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import Can from '@shared/permissions/Can';
import useCan from '@shared/permissions/useCan';
import FullDayRangePicker from '@components/shared/FullDayRangePicker';
import InventoryTransformationConceptModal from '@shared/inventory_transformations/InventoryTransformationConceptModal';
import ListPageHeader from '@shared/ListPageHeader';
import TableIndex from '@shared/table/TableIndex';
import type { SupportRule } from '@shared/table/TableIndex';
import { MultipleValue } from '@shared/table/Rules/component';

import {
  inventoryTransformationService,
  type InventoryTransformationStatus,
} from '@services/operator/inventoryTransformationService';

// TableService.getRecords 預期 `Promise<{ data, has_more }>`，但
// `inventoryTransformationService.getRecords` 因為走 `parseAdminApiResponse`
// 會回傳 `success-envelope | ApiErrorEnvelope` 的 union。Context.jsx 內部直接
// 解 `data.data / data.has_more`，碰到 error envelope 會 silent 拿到 undefined。
// 這裡 adapter 在邊界把 error envelope throw 起來，讓 react-query 走錯誤路徑。
const tableService = {
  ...inventoryTransformationService,
  getRecords: async (params: Record<string, unknown>) => {
    const res = await inventoryTransformationService.getRecords(params);
    if (!res.success) throw new Error(`InventoryTransformations getRecords failed: ${res.message}`);
    return { data: res.data, has_more: res.has_more };
  },
};

interface InventoryTransformationRow {
  id: string;
  name?: string;
  status?: InventoryTransformationStatus | string;
  applicant_name?: string;
  created_at?: string;
  [key: string]: unknown;
}

const STATUS_COLOR: Record<InventoryTransformationStatus, string> = {
  pending: 'default',
  processing: 'processing',
  applied: 'success',
  partially_applied: 'warning',
  failed: 'error',
  rejected: 'default',
};

const STATUS_LABEL_KEY: Record<InventoryTransformationStatus, string> = {
  pending: 'enums.inventory_transformation.status.pending',
  processing: 'enums.inventory_transformation.status.processing',
  applied: 'enums.inventory_transformation.status.applied',
  partially_applied: 'enums.inventory_transformation.status.partially_applied',
  failed: 'enums.inventory_transformation.status.failed',
  rejected: 'enums.inventory_transformation.status.rejected',
};

const isKnownStatus = (s?: string): s is InventoryTransformationStatus =>
  !!s && s in STATUS_COLOR;

function InventoryTransformationsList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const can = useCan();
  const [lastModified, setLastModified] = useState(0);
  const [conceptModalVisible, setConceptModalVisible] = useState(false);

  const canWrite = can('inventories:write');
  const canApprove = can('inventories:approve');

  const handleApprove = async (id: string) => {
    try {
      const response = await inventoryTransformationService.approve(id);
      if (response.success) {
        message.success(t('common.message.operation_success'));
        setLastModified(Date.now());
      } else {
        message.error(response.message || t('common.message.operation_failed'));
      }
    } catch (error) {
      console.error(error);
      message.error(t('common.message.operation_failed'));
    }
  };

  const handleReject = async (id: string) => {
    try {
      const response = await inventoryTransformationService.reject(id);
      if (response.success) {
        message.success(t('common.message.operation_success'));
        setLastModified(Date.now());
      } else {
        message.error(response.message || t('common.message.operation_failed'));
      }
    } catch (error) {
      console.error(error);
      message.error(t('common.message.operation_failed'));
    }
  };

  const columns: unknown[] = [
    {
      name: t('models.inventory_transformation.name'),
      selector: (row: InventoryTransformationRow) => row.name,
      sortable: true,
      sortFieldName: 'inventory_transformations.name',
      cell: (row: InventoryTransformationRow) => <Link to={`/inventory_transformations/${row.id}`}>{row.name}</Link>
    },
    {
      name: t('models.inventory_transformation.status'),
      selector: (row: InventoryTransformationRow) => row.status,
      cell: (row: InventoryTransformationRow) => {
        const status = row.status;
        if (isKnownStatus(status)) {
          return <Tag color={STATUS_COLOR[status]}>{t(STATUS_LABEL_KEY[status])}</Tag>;
        }
        return <Tag color="default">{status ?? ''}</Tag>;
      }
    },
    {
      name: t('models.inventory_transformation.applicant'),
      selector: (row: InventoryTransformationRow) => row.applicant_name,
    },
    {
      name: t('models.inventory_transformation.created_at'),
      selector: (row: InventoryTransformationRow) => row.created_at,
      sortable: true,
      sortFieldName: 'inventory_transformations.created_at',
    },
    {
      name: t('common.actions'),
      width: '120px',
      cell: (row: InventoryTransformationRow) => (
        <div onClick={(e) => e.stopPropagation()}>
          {row.status === 'pending' && (
            <Space>
              {canWrite && (
                <Tooltip title={t('common.button.edit')}>
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => navigate(`/inventory_transformations/${row.id}/edit`)}
                  />
                </Tooltip>
              )}

              {canApprove && (
                <Popconfirm
                  title={t('shared.inventory_transformation.approve_confirm_title')}
                  description={t('shared.inventory_transformation.approve_confirm_content', { name: row.name })}
                  onConfirm={() => handleApprove(row.id)}
                  okText={t('common.button.confirm')}
                  cancelText={t('common.button.cancel')}
                >
                  <Tooltip title={t('common.button.approve')}>
                    <Button type="text" icon={<CheckOutlined style={{ color: '#52c41a' }} />} />
                  </Tooltip>
                </Popconfirm>
              )}

              {canApprove && (
                <Popconfirm
                  title={t('shared.inventory_transformation.reject_confirm_title')}
                  description={t('shared.inventory_transformation.reject_confirm_content', { name: row.name })}
                  onConfirm={() => handleReject(row.id)}
                  okText={t('common.button.confirm')}
                  cancelText={t('common.button.cancel')}
                  okButtonProps={{ danger: true }}
                >
                  <Tooltip title={t('common.button.reject')}>
                    <Button type="text" danger icon={<CloseOutlined />} />
                  </Tooltip>
                </Popconfirm>
              )}
            </Space>
          )}
        </div>
      )
    }
  ];

  const supportRules = useMemo<SupportRule[]>(() => [
    {
      label: t('models.inventory_transformation.name'),
      component: MultipleValue,
      inputName: 'inventory_transformations.name',
      defaultPinned: true,
      createable: true,
    },
    {
      label: t('models.inventory_transformation.status'),
      component: MultipleValue,
      inputName: 'inventory_transformations.status',
      createable: false,
      defaultOptions: [
        { label: t('enums.inventory_transformation.status.pending'),           value: 'pending'           },
        { label: t('enums.inventory_transformation.status.processing'),        value: 'processing'        },
        { label: t('enums.inventory_transformation.status.applied'),           value: 'applied'           },
        { label: t('enums.inventory_transformation.status.partially_applied'), value: 'partially_applied' },
        { label: t('enums.inventory_transformation.status.failed'),            value: 'failed'            },
        { label: t('enums.inventory_transformation.status.rejected'),          value: 'rejected'          },
      ],
    },
    {
      label: t('models.inventory_transformation.created_at'),
      component: FullDayRangePicker,
      inputName: 'inventory_transformations.created_at',
    }
  ], [t]);

  return (
    <div>
      <ListPageHeader
        items={['stocks', 'stocks.transformations']}
        guideButton={
          <Tooltip title={t('concepts.inventory_transformations.tooltip')}>
            <Button
              type="text"
              size="small"
              icon={<QuestionCircleFilled style={{ color: '#1890ff' }} />}
              onClick={() => setConceptModalVisible(true)}
              style={{ padding: 0, height: 'auto', minWidth: 'auto' }}
            />
          </Tooltip>
        }
        actions={
          <Can requiredPermission="inventories:write">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/inventory_transformations/new')}
            >
              {t('common.button.new')}
            </Button>
          </Can>
        }
      />
      <TableIndex
        columns={columns}
        tableId="InventoryTransformationsTable"
        service={tableService}
        defaultQuery={{
          sorting_field: 'inventory_transformations.created_at',
          sorting_direction: 'desc',
        }}
        supportRules={supportRules}
        searchable={{
          match: 'contains',
          searchFields: [
            t('models.inventory_transformation.name'),
            t('models.inventory_transformation.note'),
          ],
        }}
        lastModified={lastModified}
        setLastModified={setLastModified}
      />
      <InventoryTransformationConceptModal
        visible={conceptModalVisible}
        onClose={() => setConceptModalVisible(false)}
      />
    </div>
  );
}

export default InventoryTransformationsList;
